import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of } from 'rxjs';
import { map, catchError, switchMap } from 'rxjs/operators';
import { SafetyCertificationService } from '../safety-certification.service';
import { CertificationActions } from './certification-monitoring.actions';

/**
 * Effects for certification monitoring
 * Handles loading certifications from OnboardingService
 */
@Injectable()
export class CertificationMonitoringEffects {
  loadProjectCertifications$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CertificationActions.loadProjectCertifications),
      switchMap(action =>
        this.certificationService.getProjectCertifications(action.projectId).pipe(
          map(certificates =>
            CertificationActions.loadProjectCertificationsSuccess({ certificates })
          ),
          catchError(error =>
            of(
              CertificationActions.loadProjectCertificationsError({
                error: error.message || 'Failed to load certifications'
              })
            )
          )
        )
      )
    )
  );

  loadExpiringSoon$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CertificationActions.loadExpiringSoon),
      switchMap(action =>
        this.certificationService
          .getCertificationsExpiringWithin(action.projectId, action.daysOut)
          .pipe(
            map(certificates =>
              CertificationActions.loadExpiringSoonSuccess({
                certificates,
                daysOut: action.daysOut
              })
            ),
            catchError(error =>
              of(
                CertificationActions.loadExpiringSoonError({
                  error: error.message || 'Failed to load expiring certifications'
                })
              )
            )
          )
      )
    )
  );

  loadExpired$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CertificationActions.loadExpired),
      switchMap(action =>
        this.certificationService.getExpiredCertifications(action.projectId).pipe(
          map(certificates =>
            CertificationActions.loadExpiredSuccess({ certificates })
          ),
          catchError(error =>
            of(
              CertificationActions.loadExpiredError({
                error: error.message || 'Failed to load expired certifications'
              })
            )
          )
        )
      )
    )
  );

  loadCertificationSummary$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CertificationActions.loadCertificationSummary),
      switchMap(action =>
        this.certificationService.getCertificationSummary(action.projectId).pipe(
          map(summary =>
            CertificationActions.loadCertificationSummarySuccess({ summary })
          ),
          catchError(error =>
            of(
              CertificationActions.loadCertificationSummaryError({
                error: error.message || 'Failed to load certification summary'
              })
            )
          )
        )
      )
    )
  );

  loadCertificationAlerts$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CertificationActions.loadCertificationAlerts),
      switchMap(action =>
        this.certificationService.generateCertificationAlerts(action.projectId).pipe(
          map(alerts =>
            CertificationActions.loadCertificationAlertsSuccess({ alerts })
          ),
          catchError(error =>
            of(
              CertificationActions.loadCertificationAlertsError({
                error: error.message || 'Failed to load certification alerts'
              })
            )
          )
        )
      )
    )
  );

  constructor(
    private actions$: Actions,
    private certificationService: SafetyCertificationService
  ) {}
}
